import DrawerNavigator from "./DrawerNavigator";
import DetailsScreen from "./screens/details/DetailsScreen";
import NewEmployeeForm from "./screens/employees/NewEmployeeForm";
import PickerScreen from "./screens/rentals/PickerScreen";
import CollectionPickerScreen from "./screens/rentals/CollectionPickerScreen";
import CreateCompanyScreen from "./screens/all-companies/CreateCompanyScreen";
import React, { useEffect } from "react";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { RootStackParamList } from "./types/navigationParams";
import { setCompany, setConfig } from "./redux/slices/authSlice";
import FormScreen from "./screens/details/FormScreen";
import DeleteConfirmationScreen from "./screens/details/DeleteConfirmationScreen";
import { useTranslation } from "react-i18next";
import { EmployeeDetailsScreen } from "./screens/employees/EmployeeDetailsScreen";
import PaymentsFormScreen from "./screens/PaymentsFormScreen";
import { useNotifications } from "./hooks/useNotifications";
import { update } from "./services/firestoreServices";
import { useAppDispatch, useAppSelector } from "./redux/store";
import { USERS_COLLECTION } from "./constants/collections";
import { fetchCompanyData } from "./services/authService";
import { getPWADisplayMode } from "./utils/utils";
import { startVersionCheck } from "./services/versionCheck";
import { serverTimestamp } from "./firebase";
import ConfigManagementScreen from "./screens/settings/ConfigManagementScreen";
import ConfigEditorScreen from "./screens/settings/ConfigEditorScreen";
import EditProfileScreen from "./screens/settings/EditProfileScreen";
import RolePermissionsScreen from "./screens/settings/RolePermissionsScreen";
import ChatScreen from "./screens/chat/ChatScreen";
import { Platform } from "react-native";
import useUserListener from "./hooks/useUserListener";
import useTaskBadge from "./hooks/useTaskBadge";
import { useScreenSize } from "./hooks/useScreenSize";
import { ImageScreen } from "./screens/ImageScreen";
import VideoScreen from "./screens/video/VideoScreen";
import FileDetailsScreen from "./screens/drive/FileDetailsScreen";
import FolderContentsScreen from "./screens/drive/FolderContentsScreen";
import { getResolvedCompanyConfig } from "./services/companyConfigService";

const Stack = createNativeStackNavigator<RootStackParamList>();
const resolveLoginPlatform = (): "web" | "ios" | "android" => {
  if (Platform.OS === "ios" || Platform.OS === "android") {
    return Platform.OS;
  }
  return "web";
};

export default function HomeStack() {
  const user = useAppSelector((state) => state.auth.user);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { LARGE_SCREEN } = useScreenSize();

  // Use the notifications hook
  const notifications = useNotifications(user.uid, user.fullName);

  // Move user listener logic into a custom hook
  useUserListener();

  // Sync app icon badge with the count of open tasks visible on the home screen
  useTaskBadge();

  useEffect(() => {
    // Start version checking
    if (Platform.OS === "web") {
      startVersionCheck();
    }
  }, []);

  // Auto-request permission when component mounts
  useEffect(() => {
    if (
      notifications.isSupported &&
      notifications.permissionStatus === "unknown"
    ) {
      notifications.requestPermission();
    }
  }, [
    notifications.isSupported,
    notifications.permissionStatus,
    notifications.requestPermission,
  ]);

  // Update config based on company type and company role permissions.
  useEffect(() => {
    let cancelled = false;

    const loadConfig = async () => {
      const config = await getResolvedCompanyConfig(
        user.company?.type,
        user.company?.id,
      );

      if (!cancelled) {
        dispatch(setConfig(config));
      }
    };

    void loadConfig().catch((error) => {
      console.error("Failed to load company config:", error);
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch, user.company?.id, user.company?.type]);

  useEffect(() => {
    if (user.uid && !user.company) {
      const fetchData = async () => {
        const companyData = await fetchCompanyData(user.email);
        if (companyData) {
          const { name, id, type } = companyData;
          await update(USERS_COLLECTION, user.uid, {
            companyId: id,
            company: {
              name: name,
              id: id,
              type: type,
            },
          });
          // Обновление данных пользователя с информацией о компании
          dispatch(
            setCompany({
              name: name,
              id: id,
              type: type,
            }),
          );
        }
      };

      void fetchData();
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (user.uid) {
      const platform = resolveLoginPlatform();
      const platformKey = `lastLoginByPlatform.${platform}`;
      const loginAt = serverTimestamp();

      void update(USERS_COLLECTION, user.uid, {
        displayMode: getPWADisplayMode(),
        lastLoginAt: loginAt,
        lastLoginPlatform: platform,
        [platformKey]: loginAt,
      }).catch((error) => {
        console.error("Failed to update login platform metadata:", error);
      });
    }
  }, [user.uid]);

  return (
    <Stack.Navigator screenOptions={{ headerBackButtonDisplayMode: "minimal" }}>
      <Stack.Screen
        name="Drawer"
        component={DrawerNavigator}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        options={{
          headerBackButtonDisplayMode: "minimal",
          title: t("Create company"),
        }}
        name="CreateCompanyScreen"
        component={CreateCompanyScreen}
      />
      <Stack.Screen
        name="DetailsScreen"
        component={DetailsScreen}
        options={{
          headerTitle: "",
          presentation: LARGE_SCREEN ? "transparentModal" : undefined,
          headerShown: !LARGE_SCREEN,
        }}
      />
      <Stack.Screen
        name="FormScreen"
        component={FormScreen}
        options={{
          headerTitle: "",
          presentation: LARGE_SCREEN ? "transparentModal" : undefined,
          headerShown: !LARGE_SCREEN,
        }}
      />
      <Stack.Screen
        name="DeleteConfirmationScreen"
        component={DeleteConfirmationScreen}
        options={{
          headerTitle: "",
          presentation: "transparentModal",
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="NewEmployeeForm"
        component={NewEmployeeForm}
        options={{
          headerTitle: t("Add new employee"),
        }}
      />
      <Stack.Screen
        name="EmployeeDetailsScreen"
        component={EmployeeDetailsScreen}
        options={{
          headerTitle: t("EmployeeDetailsScreen"),
          presentation: "transparentModal",
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="PickerScreen"
        component={PickerScreen}
        options={{
          animation: "fade",
          presentation: "transparentModal",
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="CollectionPickerScreen"
        component={CollectionPickerScreen}
        options={{
          headerShown: false,
          animation: "fade",
          presentation: "transparentModal",
        }}
      />
      <Stack.Screen
        name="PaymentsFormScreen"
        component={PaymentsFormScreen}
        options={{
          headerTitle: "",
          presentation: "transparentModal",
          headerShown: false,
        }}
      />
      {/*<Stack.Screen*/}
      {/*  name="CalendarScreen"*/}
      {/*  component={CalendarScreen}*/}
      {/*  options={{ presentation: "transparentModal", headerShown: false }}*/}
      {/*/>*/}
      <Stack.Screen
        name="ConfigManagement"
        component={ConfigManagementScreen}
        options={{
          headerTitle: "Configuration Management",
          presentation: "card",
        }}
      />
      <Stack.Screen
        name="ConfigEditor"
        component={ConfigEditorScreen}
        options={{
          headerTitle: "Configuration Editor",
          presentation: "card",
        }}
      />
      <Stack.Screen
        name="RolePermissions"
        component={RolePermissionsScreen}
        options={{
          headerTitle: t("Role permissions"),
          presentation: "card",
        }}
      />
      <Stack.Screen
        name="EditProfile"
        component={EditProfileScreen}
        options={{
          headerTitle: t("Edit Profile"),
          presentation: "card",
        }}
      />
      <Stack.Screen
        name="ChatScreen"
        component={ChatScreen}
        options={{
          headerTitle: t("Chat"),
          presentation: "card",
        }}
      />
      <Stack.Screen
        name="ImageScreen"
        component={ImageScreen}
        options={{
          headerTitle: "",
          presentation: "card",
        }}
      />
      <Stack.Screen
        name="VideoScreen"
        component={VideoScreen}
        options={{
          headerTitle: "",
          presentation: "card",
        }}
      />
      <Stack.Screen
        name="FileDetails"
        component={FileDetailsScreen}
        options={{
          headerShown: false,
          presentation: "card",
        }}
      />
      <Stack.Screen
        name="FolderContents"
        component={FolderContentsScreen}
        options={{
          headerTitle: "",
          presentation: "card",
        }}
      />
    </Stack.Navigator>
  );
}
